import { createContext, useContext, useState, useEffect, type ReactNode } from 'react'
import { jmapAuth, type StalwartAuth } from '../lib/jmap-admin'

interface AuthState {
  auth: StalwartAuth | null
  loading: boolean
  login: (username: string, password: string) => Promise<boolean>
  logout: () => void
}

const AuthContext = createContext<AuthState>({
  auth: null,
  loading: true,
  login: async () => false,
  logout: () => {},
})

const STORAGE_KEY = 'envelo_jmap_mail_auth'

export function JMAPAuthProvider({ children }: { children: ReactNode }) {
  const [auth, setAuth] = useState<StalwartAuth | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as StalwartAuth
        jmapAuth(parsed).then((ok) => {
          if (ok) setAuth(parsed)
          else localStorage.removeItem(STORAGE_KEY)
          setLoading(false)
        })
      } catch {
        localStorage.removeItem(STORAGE_KEY)
        setLoading(false)
      }
    } else {
      setLoading(false)
    }
  }, [])

  const login = async (username: string, password: string): Promise<boolean> => {
    const ok = await jmapAuth({ username, password })
    if (ok) {
      const credentials = { username, password }
      setAuth(credentials)
      localStorage.setItem(STORAGE_KEY, JSON.stringify(credentials))
    }
    return ok
  }

  const logout = () => {
    setAuth(null)
    localStorage.removeItem(STORAGE_KEY)
  }

  return (
    <AuthContext.Provider value={{ auth, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useJMAPAuth() {
  return useContext(AuthContext)
}

// Backward compatibility alias
export const useStalwartAuth = useJMAPAuth
export const StalwartAuthProvider = JMAPAuthProvider
